/**
 * ResumoEspecificacao - Coluna lateral enxuta (FASE 2)
 *
 * Removidos: Projeto e Modelo (já estão no breadcrumb / linha 2 do header)
 * Mantidos: Cliente, Previsão de Entrega, Custo Total, Alerta de itens sem fornecedor
 * Adicionados: Itens concluídos / pendentes (donut visual) e atalho rápido
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calendar, ClipboardList, AlertTriangle, User, CheckCircle2, Circle } from 'lucide-react';
import { formatCurrency } from './constantes';

const ResumoEspecificacao = ({
  project,
  embarcacao,
  vesselModel,
  progresso,
  itensSemFornecedor,
  custoTotal,
  compradoTotal = 0,
  custoEstimadoProjeto = null,
  podeVerCustos,
  totalItens = 0,
  itensConcluidos = 0,
}) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const pendentes = Math.max(totalItens - itensConcluidos, 0);

  return (
    <Card className="sticky top-4">
      <CardHeader className="pb-3 border-b">
        <CardTitle className="text-base flex items-center gap-2 text-gray-700">
          <ClipboardList className="h-4 w-4" />
          Informações
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {/* Cliente */}
        <div className="space-y-1">
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <User className="h-3 w-3" />
            Cliente
          </p>
          <p className="text-sm font-medium text-gray-800">{project?.client || '-'}</p>
        </div>

        {/* Previsão de Entrega */}
        <div className="space-y-1">
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <Calendar className="h-3 w-3" />
            Previsão de Entrega
          </p>
          <p className="text-sm font-medium text-gray-800">{formatDate(project?.delivery_date)}</p>
        </div>

        {/* Status do Checklist */}
        <div className="pt-3 border-t space-y-2">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
            Checklist
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-green-50 border border-green-200 rounded-md p-2">
              <div className="flex items-center gap-1.5 text-green-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Definidos</span>
              </div>
              <p className="text-xl font-bold text-green-900 leading-tight">{itensConcluidos}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
              <div className="flex items-center gap-1.5 text-gray-600">
                <Circle className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Pendentes</span>
              </div>
              <p className="text-xl font-bold text-gray-900 leading-tight">{pendentes}</p>
            </div>
          </div>
        </div>

        {/* Custo Estimado — mesmo valor/nome da aba Financeiro do projeto (custo_estimado_manual).
            Cai para o custo do checklist da ficha apenas quando o projeto ainda não tem
            orçamento manual definido, para não ficar com o card vazio. */}
        {podeVerCustos && (
          <div className="pt-3 border-t space-y-1">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Custo Estimado
            </p>
            <p className="text-2xl font-bold text-green-700 leading-tight">
              {formatCurrency((custoEstimadoProjeto > 0 ? custoEstimadoProjeto : custoTotal) || 0)}
            </p>
          </div>
        )}

        {/* Custo Efetivo (soma real das POs aprovadas do projeto) — mesmo nome/valor da aba Financeiro */}
        {podeVerCustos && compradoTotal > 0 && (
          <div className="pt-3 border-t space-y-1" data-testid="resumo-custo-comprado">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Custo Efetivo
            </p>
            <p className="text-2xl font-bold text-emerald-700 leading-tight">
              {formatCurrency(compradoTotal)}
            </p>
            {(custoEstimadoProjeto > 0 || custoTotal > 0) && (
              <p className="text-xs text-gray-500">
                {Math.min((compradoTotal / (custoEstimadoProjeto > 0 ? custoEstimadoProjeto : custoTotal)) * 100, 999).toFixed(1)}% do Custo Estimado
              </p>
            )}
          </div>
        )}

        {/* Alerta de itens sem fornecedor */}
        {itensSemFornecedor > 0 && (
          <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-start gap-2 text-yellow-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p className="text-xs">
                <strong>{itensSemFornecedor}</strong> item(ns) marcado(s) sem fornecedor definido
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ResumoEspecificacao;
